// Comuni PA (demo SurvPA): zone da gpkg nel backend legacy (/api/demo/{city}/*),
// normativa di zona in anteprima, ricerca particella, PDF e DXF.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { Button, Check, Dock, DockSep, Field, IconButton, LoadingOverlay, Panel, Tabs } from "../components/ui";
import LegendPanel from "../components/LegendPanel";
import { BottomBar, FootLinks, ZonePicker } from "../components/MapBits";
import { useToast } from "../components/Toast";
import useDraw from "../hooks/useDraw";
import useMeasure from "../hooks/useMeasure";
import { BACKEND } from "../lib/config";
import { getJson, mapLimit, postForDownload } from "../lib/http";
import { bindPattern, createHighlighter, ensurePattern, formatFoglio, legacyZoneStyle, zonesAt } from "../lib/zones";

const CATASTO_GROUP = "Catasto";
const PARCEL_STYLE = { color: "#84e34c", weight: 3, fillColor: "#84e34c", fillOpacity: 0.12 };

function wrapForIframe(html = "") {
  const inject =
    '<meta name="viewport" content="width=device-width,initial-scale=0.8">' +
    "<style>body{font-family:Arial,sans-serif;font-size:11px;margin:10px}" +
    "*{box-sizing:border-box}img{max-width:100%;height:auto}" +
    "table{border-collapse:collapse}td,th{padding:3px 6px;border:1px solid #ccc;vertical-align:top}</style>";
  if (/<html[\s>]/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${inject}`);
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${inject}</head><body>${html}</body></html>`;
}

// "C615_000100.1083" → { comune: "C615", foglio: "1", particella: "1083" }
function parseNcr(ncr = "") {
  const [comune, rest] = ncr.split("_");
  const [foglioRaw, particella] = (rest || "").split(".");
  return { comune: comune || "—", foglio: formatFoglio(foglioRaw), particella: particella || "—" };
}

// Selezione della fonte (disegno o particella) e delle categorie per PDF e DXF.
function SourceAndClasses({ mode, setMode, foglio, setFoglio, particella, setParticella, classes, selected, setSelected, idPrefix }) {
  return (
    <>
      <Tabs value={mode} onChange={setMode} options={[{ value: "drawing", label: "Disegno" }, { value: "cadastral", label: "Particella" }]} />
      {mode === "cadastral" && (
        <div className="sm-field-row">
          <Field label="Foglio" htmlFor={`${idPrefix}-foglio`}>
            <input id={`${idPrefix}-foglio`} className="sm-input sm-input--mono" value={foglio} placeholder="es. 10B" onChange={(e) => setFoglio(e.target.value)} />
          </Field>
          <Field label="Particella" htmlFor={`${idPrefix}-particella`}>
            <input id={`${idPrefix}-particella`} className="sm-input sm-input--mono" value={particella} placeholder="es. 187" onChange={(e) => setParticella(e.target.value)} />
          </Field>
        </div>
      )}
      {classes.length > 0 && (
        <>
          <div style={{ display: "flex", alignItems: "center" }}>
            <h3 className="sm-section-title" style={{ flex: 1 }}>Categorie</h3>
            <div className="sm-list-actions">
              <button type="button" className="sm-link" onClick={() => setSelected(new Set(classes))}>Tutte</button>
              <button type="button" className="sm-link" onClick={() => setSelected(new Set())}>Nessuna</button>
            </div>
          </div>
          <div className="sm-scroll-list" style={{ maxHeight: 220 }}>
            {classes.map((cls) => (
              <Check key={cls} checked={selected.has(cls)} onChange={() => setSelected((prev) => {
                const n = new Set(prev);
                if (n.has(cls)) n.delete(cls); else n.add(cls);
                return n;
              })}>
                {cls}
              </Check>
            ))}
          </div>
        </>
      )}
    </>
  );
}

export default function PaMap({ city, onMap }) {
  const toast = useToast();
  const api = `${BACKEND}/api/demo/${city.key}`;
  const elRef = useRef(null);
  const [map, setMap] = useState(null);
  const baseRef = useRef(null);
  const layersRef = useRef({ groups: {}, items: {} });
  const zoneLayersRef = useRef([]);
  const parcelRef = useRef(null);
  const highlighter = useMemo(createHighlighter, []);

  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [legend, setLegend] = useState([]);
  const [isGoogle, setIsGoogle] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const previewRef = useRef(previewMode);
  previewRef.current = previewMode;
  const [panel, setPanel] = useState(null);       // legend | parcel | pdf | dxf
  const [picker, setPicker] = useState(null);
  const [info, setInfo] = useState(null);         // { title, rows } per particella / zona
  const [doc, setDoc] = useState(null);           // normativa di zona { name, html }
  const [docPos, setDocPos] = useState({ top: 80, left: 380, width: 440, height: 520 });
  const dragRef = useRef(null);

  const [classes, setClasses] = useState([]);
  const [pdfMode, setPdfMode] = useState("drawing");
  const [pdfFoglio, setPdfFoglio] = useState("");
  const [pdfParticella, setPdfParticella] = useState("");
  const [pdfClasses, setPdfClasses] = useState(new Set());
  const [pdfBusy, setPdfBusy] = useState(false);
  const [dxfMode, setDxfMode] = useState("drawing");
  const [dxfFoglio, setDxfFoglio] = useState("");
  const [dxfParticella, setDxfParticella] = useState("");
  const [dxfClasses, setDxfClasses] = useState(new Set());
  const [dxfCatasto, setDxfCatasto] = useState(false);
  const [dxfBusy, setDxfBusy] = useState(false);
  const [foglio, setFoglio] = useState("");
  const [particella, setParticella] = useState("");
  const [parcelError, setParcelError] = useState("");

  const draw = useDraw(map);
  const measure = useMeasure(map);
  const busyRef = useRef(false);
  busyRef.current = !!draw.mode || measure.active;

  // ── Mappa e confini comunali ─────────────────────────────────────────────
  useEffect(() => {
    const m = L.map(elRef.current, { zoomControl: false, minZoom: 6, maxZoom: 20 }).setView([city.lat || 43.6, city.lon || 13.4], city.zoom || 14);
    L.control.zoom({ position: "bottomright" }).addTo(m);
    const catastoPane = m.createPane("sm-catasto");
    catastoPane.style.zIndex = 420;
    catastoPane.style.pointerEvents = "none";
    m.on("movestart", () => { highlighter.clear(); setPicker(null); });
    setMap(m);
    onMap(m);
    return () => {
      onMap(null);
      setMap(null);
      m.remove();
    };
  }, [city.key]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!map) return;
    getJson(`${BACKEND}/api/${city.key}/cityconf.json`)
      .then((cfg) => {
        if (cfg["city view coords"]) {
          const [lat, lng] = cfg["city view coords"].split(",").map((v) => parseFloat(v.trim()));
          map.setView([lat, lng], parseInt(cfg["city view zoom"] || "15", 10));
        }
        if (cfg["min zoom"]) map.setMinZoom(parseInt(cfg["min zoom"], 10));
        if (cfg["max zoom"]) map.setMaxZoom(parseInt(cfg["max zoom"], 10));
        if (cfg["max-left-pan"] && cfg["max-right-pan"] && cfg["max-top-pan"] && cfg["max-down-pan"]) {
          const b = L.latLngBounds(
            [parseFloat(cfg["max-down-pan"]), parseFloat(cfg["max-left-pan"])],
            [parseFloat(cfg["max-top-pan"]), parseFloat(cfg["max-right-pan"])],
          );
          map.setMaxBounds(b);
          onMap(map, b);
        }
      })
      .catch(() => toast.error("Configurazione del comune non disponibile."));
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Basemap: OSM locale del comune o Google ──────────────────────────────
  useEffect(() => {
    if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = L.tileLayer(
      isGoogle ? `${BACKEND}/api/google-tiles/{z}/{x}/{y}.png` : `${BACKEND}/tiles/${city.key}/tiles/{z}/{x}/{y}.png`,
      {
        maxZoom: 20,
        attribution: isGoogle ? "© Google" : "© OpenStreetMap contributors",
      },
    ).addTo(map);
  }, [map, isGoogle, city.key]);

  // ── Zone dal gpkg ────────────────────────────────────────────────────────
  const selectZone = useCallback(async (layer) => {
    highlighter.apply(layer);
    if (!previewRef.current) return;
    try {
      const data = await getJson(`${api}/zone-info?name=${encodeURIComponent(layer._zoneName)}`);
      if (data.html) setDoc({ name: data.name || layer._zoneName, html: data.html });
      else toast.info(`Nessuna normativa caricata per ${layer._zoneName}.`);
    } catch {
      toast.error("Normativa di zona non disponibile.");
    }
  }, [api, highlighter, toast]);

  useEffect(() => {
    if (!map) return undefined;
    let cancelled = false;

    const onZoneClick = (layer) => (e) => {
      if (busyRef.current) return;
      const candidates = zonesAt(zoneLayersRef.current, e.latlng);
      if (candidates.length <= 1) { selectZone(layer); setPicker(null); return; }
      const pt = map.latLngToContainerPoint(e.latlng);
      const rect = map.getContainer().getBoundingClientRect();
      setPicker({
        x: rect.left + pt.x,
        y: rect.top + pt.y,
        items: candidates.map((l) => ({ label: l._zoneName, onPick: () => selectZone(l) })),
      });
    };

    const build = async ({ name, style = {}, tag }) => {
      const data = await getJson(`${api}/geojson/${encodeURIComponent(name)}`);
      const group = tag || "Livelli";
      if (style.isNametag) {
        const layer = L.geoJSON(data, {
          pointToLayer: (f, latlng) =>
            L.circleMarker(latlng, { radius: 0, fillOpacity: 0, stroke: false })
              .bindTooltip(f.properties?.name || "", { permanent: true, direction: "center", className: "nametag-label" }),
        });
        return { id: name, label: name, group, layer, swatch: { background: "transparent", borderColor: "#f4f6fb" } };
      }
      const { style: base, pattern, swatch } = legacyZoneStyle(style);
      const patternId = pattern ? ensurePattern(map, pattern) : null;
      const layer = L.geoJSON(data, {
        style: () => base,
        onEachFeature: (_f, l) => {
          l._baseStyle = { ...base };
          l._zoneName = name;
          bindPattern(l, patternId);
          l.bindTooltip(name, { sticky: true });
          zoneLayersRef.current.push(l);
          l.on("click", onZoneClick(l));
        },
      });
      return { id: name, label: name, group, layer, swatch };
    };

    (async () => {
      setLoading(true);
      try {
        const raw = await getJson(`${api}/layers`);
        // il gpkg ha una riga per poligono: /layers ripete lo stesso nome
        const seen = new Set();
        const layers = raw.filter((l) => (seen.has(l.name) ? false : seen.add(l.name)));
        const built = (await mapLimit(layers, 6, build, (p) => !cancelled && setProgress(p))).filter(Boolean);
        if (cancelled) return;

        const groups = {};
        const items = {};
        built.forEach((b) => {
          if (!groups[b.group]) groups[b.group] = L.layerGroup();
          groups[b.group].addLayer(b.layer);
          items[b.id] = b;
        });
        const names = Object.keys(groups);
        if (names.length) groups[names[0]].addTo(map);

        groups[CATASTO_GROUP] = L.layerGroup([L.tileLayer.wms(`${BACKEND}/api/catasto`, {
          layers: "CP.CadastralZoning,CP.CadastralParcel,fabbricati,simbolo_graffa,vestizioni",
          format: "image/png", transparent: true, version: "1.1.1", opacity: 0.9,
          minZoom: 15, maxZoom: 20, attribution: "© Agenzia delle Entrate", pane: "sm-catasto",
        })]);
        layersRef.current = { groups, items };

        setLegend([
          ...names.map((name, i) => ({
            id: name, name, visible: i === 0,
            items: built.filter((b) => b.group === name).map((b) => ({ id: b.id, label: b.label, swatch: b.swatch, visible: true })),
          })),
          { id: CATASTO_GROUP, name: "Catasto (da zoom 15, tasto destro per i dati)", visible: false },
        ]);

        const cls = [...new Set(layers.map((l) => l.class).filter(Boolean))].sort();
        setClasses(cls);
        setPdfClasses(new Set(cls));
        setDxfClasses(new Set(cls));
      } catch {
        if (!cancelled) toast.error("Impossibile caricare i livelli del comune.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      Object.values(layersRef.current.groups).forEach((g) => map.removeLayer(g));
      layersRef.current = { groups: {}, items: {} };
      zoneLayersRef.current = [];
    };
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Tasto destro con il catasto acceso: dati della particella dal WMS dell'Agenzia.
  useEffect(() => {
    if (!map) return undefined;
    const onContext = async (e) => {
      const cat = layersRef.current.groups[CATASTO_GROUP];
      if (!cat || !map.hasLayer(cat) || map.getZoom() < 15) return;
      try {
        const data = await getJson(`${BACKEND}/api/catasto-info?lat=${e.latlng.lat}&lng=${e.latlng.lng}`);
        if (data.error || !data.features?.length) {
          setInfo({ title: "Catasto", text: "Nessuna particella catastale in questo punto." });
          return;
        }
        const ncr = Object.entries(data.features[0]).find(([k]) => k.toLowerCase().includes("nationalcadastral"))?.[1] || "";
        const p = parseNcr(ncr);
        setInfo({ title: "Particella catastale", rows: [["Comune", p.comune], ["Foglio", p.foglio], ["Particella", p.particella]] });
      } catch {
        toast.error("Dati catastali non disponibili.");
      }
    };
    map.on("contextmenu", onContext);
    return () => map.off("contextmenu", onContext);
  }, [map, toast]);

  // ── Legenda ──────────────────────────────────────────────────────────────
  const toggleGroup = (id) => {
    const g = layersRef.current.groups[id];
    const grp = legend.find((x) => x.id === id);
    if (!g || !grp || !map) return;
    if (grp.visible) map.removeLayer(g); else g.addTo(map);
    setLegend((list) => list.map((x) => (x.id === id ? { ...x, visible: !grp.visible } : x)));
  };

  const setItems = (groupId, on, only) => {
    const g = layersRef.current.groups[groupId];
    const grp = legend.find((x) => x.id === groupId);
    if (!g || !grp?.items) return;
    const ids = only ? new Set(only.map((i) => i.id)) : null;
    grp.items.forEach((it) => {
      if (ids && !ids.has(it.id)) return;
      const layer = layersRef.current.items[it.id]?.layer;
      if (layer) { if (on) g.addLayer(layer); else g.removeLayer(layer); }
    });
    setLegend((list) => list.map((x) => (x.id !== groupId ? x : {
      ...x,
      items: x.items.map((it) => (!ids || ids.has(it.id) ? { ...it, visible: on } : it)),
    })));
  };

  const toggleItem = (groupId, itemId) => {
    const it = legend.find((g) => g.id === groupId)?.items?.find((i) => i.id === itemId);
    if (it) setItems(groupId, !it.visible, [it]);
  };

  // ── Particelle ───────────────────────────────────────────────────────────
  const fetchParcel = async (f, p) => {
    const data = await getJson(`${api}/catasto-search?foglio=${encodeURIComponent(f)}&particella=${encodeURIComponent(p)}`);
    if (data.error) throw new Error(data.error);
    return data;
  };

  const searchParcel = async () => {
    const f = foglio.trim();
    const p = particella.trim();
    if (!f || !p) { setParcelError("Inserisci foglio e particella."); return; }
    setParcelError("");
    try {
      const data = await fetchParcel(f, p);
      if (parcelRef.current) map.removeLayer(parcelRef.current);
      parcelRef.current = L.geoJSON(data.geojson, { style: PARCEL_STYLE, interactive: false }).addTo(map);
      const { south, west, north, east } = data.bbox;
      map.fitBounds([[south, west], [north, east]], { padding: [60, 60], maxZoom: 18 });
      const parsed = parseNcr(data.ncr);
      setInfo({ title: "Particella catastale", rows: [["Comune", parsed.comune], ["Foglio", parsed.foglio], ["Particella", parsed.particella]] });
      setPanel(null);
    } catch (e) {
      setParcelError(e.message && e.message !== "Errore 404" ? e.message : "Particella non trovata.");
    }
  };

  // Disegno o particella → Feature GeoJSON per PDF/DXF.
  const sourceFeature = async (mode, f, p) => {
    if (mode === "drawing") {
      if (!draw.shape) throw new Error("Disegna prima un poligono o un rettangolo.");
      return draw.shape;
    }
    if (!f.trim() || !p.trim()) throw new Error("Inserisci foglio e particella.");
    const data = await fetchParcel(f.trim(), p.trim());
    return { type: "Feature", geometry: data.geojson, properties: {} };
  };

  const exportPdf = async () => {
    if (pdfClasses.size === 0) { toast.info("Seleziona almeno una categoria."); return; }
    setPdfBusy(true);
    try {
      const feature = await sourceFeature(pdfMode, pdfFoglio, pdfParticella);
      await postForDownload(`${api}/query_pdf`, { ...feature, classes: [...pdfClasses] }, "norme_applicate.pdf", {
        fallbackError: "Nessuna zona trovata o area troppo grande.",
      });
      toast.ok("PDF della normativa scaricato.");
    } catch (e) {
      toast.error(e.message || "Estrazione del PDF non riuscita.");
    } finally {
      setPdfBusy(false);
    }
  };

  const exportDxf = async () => {
    if (dxfClasses.size === 0 && !dxfCatasto) { toast.info("Seleziona almeno una categoria o includi il catasto."); return; }
    setDxfBusy(true);
    try {
      const feature = await sourceFeature(dxfMode, dxfFoglio, dxfParticella);
      await postForDownload(`${api}/export_dxf`, { ...feature, classes: [...dxfClasses], include_catasto: dxfCatasto }, "export.dxf", {
        fallbackError: "Esportazione DXF non riuscita.",
      });
      toast.ok("DXF scaricato.");
    } catch (e) {
      toast.error(e.message || "Esportazione DXF non riuscita.");
    } finally {
      setDxfBusy(false);
    }
  };

  // ── Pannello normativa trascinabile ──────────────────────────────────────
  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.x;
      const dy = e.clientY - d.y;
      setDocPos((p) => (d.kind === "move"
        ? { ...p, left: Math.max(0, d.left + dx), top: Math.max(56, d.top + dy) }
        : { ...p, width: Math.max(280, d.width + dx), height: Math.max(200, d.height + dy) }));
    };
    const onUp = () => { dragRef.current = null; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);
  const startDrag = (kind) => (e) => {
    dragRef.current = { kind, x: e.clientX, y: e.clientY, ...docPos };
    e.preventDefault();
    e.stopPropagation();
  };

  // ── Strumenti ────────────────────────────────────────────────────────────
  const togglePanel = (name) => setPanel((p) => (p === name ? null : name));
  const startDraw = (kind) => { measure.stop(); draw.start(kind); };
  const toggleMeasure = () => {
    if (measure.active) { measure.stop(); return; }
    draw.cancel();
    measure.start();
  };

  return (
    <>
      <div ref={elRef} className="sm-map" />

      <Dock>
        <IconButton icon="layers" label="Legenda" active={panel === "legend"} onClick={() => togglePanel("legend")} />
        <IconButton icon="eye" label={previewMode ? "Anteprima normativa: attiva" : "Anteprima normativa al clic su una zona"} active={previewMode}
          onClick={() => { setPreviewMode((v) => !v); if (previewMode) setDoc(null); }} />
        <IconButton icon="grid" label="Cerca particella" active={panel === "parcel"} onClick={() => togglePanel("parcel")} />
        <DockSep />
        <IconButton icon="polygon" label="Disegna un poligono" active={draw.mode === "polygon"} onClick={() => startDraw("polygon")} />
        <IconButton icon="square" label="Disegna un rettangolo" active={draw.mode === "rectangle"} onClick={() => startDraw("rectangle")} />
        <IconButton icon="trash" label="Cancella il disegno" disabled={!draw.shape} onClick={draw.clear} />
        <DockSep />
        <IconButton icon="file-text" label="Estrai norme applicate (PDF)" active={panel === "pdf"} onClick={() => togglePanel("pdf")} />
        <IconButton icon="download" label="Esporta DXF" active={panel === "dxf"} onClick={() => togglePanel("dxf")} />
        <IconButton icon="ruler" label="Misura distanze" active={measure.active} onClick={toggleMeasure} />
        <DockSep />
        <IconButton icon="map" label={isGoogle ? "Basemap: passa a OpenStreetMap" : "Basemap: passa a Google"} onClick={() => setIsGoogle((v) => !v)} />
      </Dock>

      {panel === "legend" && (
        <LegendPanel groups={legend} onToggleGroup={toggleGroup} onToggleItem={toggleItem} onSetItems={setItems} filterable onClose={() => setPanel(null)} />
      )}

      {panel === "parcel" && (
        <Panel title="Cerca particella" onClose={() => setPanel(null)} footer={<Button block icon="search" onClick={searchParcel}>Cerca</Button>}>
          <div className="sm-field-row">
            <Field label="Foglio" htmlFor="pa-foglio">
              <input id="pa-foglio" className="sm-input sm-input--mono" value={foglio} placeholder="es. 10B"
                onChange={(e) => setFoglio(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchParcel()} />
            </Field>
            <Field label="Particella" htmlFor="pa-particella">
              <input id="pa-particella" className="sm-input sm-input--mono" value={particella} placeholder="es. 187"
                onChange={(e) => setParticella(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchParcel()} />
            </Field>
          </div>
          {parcelError && <p className="sm-panel-error">{parcelError}</p>}
        </Panel>
      )}

      {panel === "pdf" && (
        <Panel title="Norme applicate (PDF)" onClose={() => setPanel(null)}
          footer={<Button block variant="document" icon="file-text" busy={pdfBusy} onClick={exportPdf}>Estrai PDF</Button>}>
          <SourceAndClasses idPrefix="pdf" mode={pdfMode} setMode={setPdfMode} foglio={pdfFoglio} setFoglio={setPdfFoglio}
            particella={pdfParticella} setParticella={setPdfParticella} classes={classes} selected={pdfClasses} setSelected={setPdfClasses} />
        </Panel>
      )}

      {panel === "dxf" && (
        <Panel title="Esporta DXF" onClose={() => setPanel(null)}
          footer={<Button block icon="download" busy={dxfBusy} onClick={exportDxf}>Esporta DXF</Button>}>
          <SourceAndClasses idPrefix="dxf" mode={dxfMode} setMode={setDxfMode} foglio={dxfFoglio} setFoglio={setDxfFoglio}
            particella={dxfParticella} setParticella={setDxfParticella} classes={classes} selected={dxfClasses} setSelected={setDxfClasses} />
          <Check checked={dxfCatasto} onChange={() => setDxfCatasto((v) => !v)}>Includi il layer catasto</Check>
        </Panel>
      )}

      {info && (
        <Panel side="right" title={info.title} onClose={() => setInfo(null)}>
          {info.text && <p className="sm-panel-text">{info.text}</p>}
          {info.rows && (
            <dl className="sm-kv">
              {info.rows.map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd>{v}</dd></React.Fragment>)}
            </dl>
          )}
        </Panel>
      )}

      {doc && (
        <div className="sm-zone-doc" style={docPos}>
          <header className="sm-panel-head" onMouseDown={startDrag("move")}>
            <h2 className="sm-panel-title" style={{ margin: 0 }}>{doc.name}</h2>
            <IconButton icon="x" label="Chiudi" onMouseDown={(e) => e.stopPropagation()} onClick={() => setDoc(null)} />
          </header>
          {/* senza allow-scripts l'HTML della normativa non esegue JavaScript */}
          <iframe title={doc.name} srcDoc={wrapForIframe(doc.html)} sandbox="allow-same-origin" />
          <div className="sm-zone-doc-resize" onMouseDown={startDrag("resize")} />
        </div>
      )}

      <ZonePicker picker={picker} onClose={() => setPicker(null)} />

      <BottomBar measure={measure} draw={draw}>
        <span className="sm-label">Area disegnata</span>
        <Button variant="document" size="sm" icon="file-text" onClick={() => { setPdfMode("drawing"); setPanel("pdf"); }}>Norme PDF</Button>
        <Button size="sm" icon="download" onClick={() => { setDxfMode("drawing"); setPanel("dxf"); }}>DXF</Button>
        <Button variant="ghost" size="sm" icon="trash" onClick={draw.clear}>Cancella</Button>
      </BottomBar>

      <img src={`${api}/stemma`} alt="Stemma comunale" className="sm-stemma" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <FootLinks />
      {loading && <LoadingOverlay label="Caricamento strati urbanistici" progress={progress} />}
    </>
  );
}
